import { notify } from 'helpers/NotifyHelper';
import { t } from 'i18next';
import { useState } from 'react';
import { getNotificationInfo } from 'store/admin/actions/AdminNotificationAction';
import { notificationMarkedRead } from 'store/admin/slices/AdminGlobalSlice';
import { useAppDispatch } from 'store/hooks';

interface NotificationMessage {
  to: string;
  subject: string;
  message: string;
  attachment: string | null;
  deleted_at: string | null;
  created_by: string;
  created_at: string;
  id: string;
}

interface NotificationItem {
  id: string;
  type: string;
  user: {
    _id: string;
    first_name: string;
    last_name: string;
    email: string;
  };
  read_at: string | null;
  created_at: string;
  message: NotificationMessage;
  // the order the notification is about (group order number, and the sub-order when known)
  order_number?: string | null;
  order_id?: string | null;
}

interface NotificationCardProps {
  item: NotificationItem;
  // called once the card has sent the user somewhere, so the dropdown can close
  onNavigate?: () => void;
}

function NotificationCard({ item }: NotificationCardProps) {
  const { user, message, read_at, created_at } = item;
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const initials = `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });

  // POST /notification-info is what sets read_at; on success the card leaves the unread list
  const handleMarkRead = async () => {
    if (loading || read_at) return;
    const id = item.id;
    if (!id) {
      notify.error(t('something_went_wrong'));
      return;
    }
    try {
      setLoading(true);
      // marks the notification read
      const response: any = await getNotificationInfo({ id });
      if (!response?.status) {
        notify.error(response?.message ?? t('something_went_wrong'));
        return;
      }
      dispatch(notificationMarkedRead(id));
    } catch (error: any) {
      notify.error(error.message ?? t('something_went_wrong'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`notification-card ${loading ? 'notification-card--loading' : ''} ${!read_at ? 'notification-card--unread' : ''}`}
    >
      <div className="nc-header">
        <div className="nc-avatar">{initials}</div>
        <div className="nc-meta">
          <div className="nc-meta-top">
            <span className="nc-name">
              {user.first_name} {user.last_name}
            </span>
            <div className="nc-time-row">
              {!read_at && <span className="nc-unread-dot" />}
              <span className="nc-time">{formatDate(created_at)}</span>
            </div>
          </div>
          <span className="nc-email">{user.email}</span>
        </div>
      </div>
      <div className="nc-body">
        <div className="nc-subject-row">
          <span className="nc-subject">{message.subject}</span>
        </div>
        <p className="nc-preview">{message.message}</p>
        {!read_at && (
          <div className="nc-actions">
            <button
              type="button"
              className="nc-mark-read"
              onClick={handleMarkRead}
              disabled={loading}
            >
              {t('mark_as_read')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default NotificationCard;
